import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router';
import { BreadcrumbsItem } from 'wix-style-react';
import { IPage, PageRoute, Pages } from '@src/types';
import { InjectedTranslateProps, translate } from 'react-i18next';
import { createBreadcrumbsFromPath, getPageFromPath, translateBreadcrumbs } from '@src/context/navigation-store/utils';
import { hasPendingChanges } from '../selectors/app-settings';
import { AppContext } from '../store';
import { getNavigationActions, INavigationActions } from './navigation-listener';

export const BMPendingNavigation = 'BM_PENDING_NAV';
type PendingNavigation = false | PageRoute | typeof BMPendingNavigation;

export interface NavigationProps extends RouteComponentProps, InjectedTranslateProps {
  pendingChanges: boolean;
  cancelPendingChanges(): void;
}

export type INavigationStoreState = {
  pageData: IPage;
  breadcrumbItems: BreadcrumbsItem[];
  pendingRoute: PendingNavigation;
};

export interface IPrivateNavigationState {
  pathname: string;
}

export type NavigationStore = INavigationStoreState & INavigationActions;
export type NavigationState = INavigationStoreState & IPrivateNavigationState;

export const NavigationContext = React.createContext({} as NavigationStore);

class StoreProvider extends React.PureComponent<NavigationProps, NavigationState> {
  readonly state: NavigationState = {
    pathname: '',
    pageData: Pages[PageRoute.ROOT],
    breadcrumbItems: [],
    pendingRoute: false,
  };

  private readonly actions: INavigationActions;

  constructor(props: NavigationProps) {
    super(props);
    this.actions = getNavigationActions({
      getProps: () => ({
        ...this.props,
        mounted: this._mounted,
      }),
      getState: () => this.state,
      setState: (state) => this.setState(state as NavigationState),
    });
  }

  private _mounted = false;
  componentDidMount() {
    this._mounted = true;
  }

  componentWillUnmount() {
    this._mounted = false;
  }

  static getDerivedStateFromProps(props: NavigationProps, state: NavigationState) {
    const {
      t,
      location: { pathname },
    } = props;
    if (pathname === state.pathname) {
      return null;
    }

    const breadcrumbItems = createBreadcrumbsFromPath(pathname);
    const pageData = getPageFromPath(pathname);
    const translatedValue = translateBreadcrumbs({ t, breadcrumbItems });

    return {
      breadcrumbItems: translatedValue,
      pageData,
      pathname,
    };
  }

  render() {
    return (
      <NavigationContext.Provider
        value={
          {
            ...this.state,
            ...this.actions,
          } as NavigationStore
        }
      >
        {this.props.children}
      </NavigationContext.Provider>
    );
  }
}

export const NavigationStoreWithRouter = translate()(withRouter(StoreProvider));

export const NavigationStoreProvider = ({ children }: any) => {
  const store = React.useContext(AppContext);
  return (
    <NavigationStoreWithRouter
      pendingChanges={hasPendingChanges(store)}
      cancelPendingChanges={store.cancelPendingChanges}
    >
      {children}
    </NavigationStoreWithRouter>
  );
};
